import { isFeatureEnabled, isDevFeature, type FeatureName } from '@shared/features'
import { isProduction } from '@/utils/env'
import { ConfigService } from '@/services/ConfigService'
import { logger } from '@/utils/logger'
import type { NavItemConfig } from '@/components/layout/NavItem'
import type { FrontendFeature } from './types'
import type { ReactElement } from 'react'

type FeatureLoader = () => Promise<{ default: FrontendFeature }>

// Not all features have frontend components - some are backend-only
// (e.g., entitlements, auth)
const loaders: Partial<Record<FeatureName, FeatureLoader>> = {
  admin: () => import('./admin'),
  profile: () => import('./profile'),
  docs: () => import('./docs'),
  devtools: () => import('./devtools'),
  files: () => import('./files'),
  about: () => import('./about'),
  import: () => import('./import'),
  workspace: () => import('./workspace'),
  overtime: () => import('./overtime'),
  teams: () => import('./teams'),
  'ai-usage': () => import('./ai-usage'),
  'pipe-usage': () => import('./pipe-usage'),
}

/**
 * Whether a feature loads, preferring the backend's RESOLVED verdict.
 *
 * `/api/config` returns a features map with the deployment's FEATURE_FLAGS
 * overrides already applied, and `ConfigService.initialize()` runs before
 * `initFeatures()` in main.tsx. Reading only the static flag meant a
 * default-off feature could never be switched on in the browser: no nav item,
 * no route, and no error to explain it. Nothing caught that for a long time
 * because every registered frontend feature was statically enabled.
 *
 * `undefined` from the resolver means "no opinion" — config failed to load, or
 * the feature is absent from the map — and falls back to the static flag, so a
 * failed /api/config cannot blank the whole navigation.
 *
 * The devOnly guard is re-applied on top regardless: dev-only features are
 * LOCAL-only, and no deployment override may resurrect one in a production
 * build.
 */
function featureIsOn(name: FeatureName, prod: boolean): boolean {
  if (isDevFeature(name) && prod) return false
  return ConfigService.getResolvedFeature(name) ?? isFeatureEnabled(name, prod)
}

/**
 * The same verdict `initFeatures` used, for code outside the registry.
 *
 * A feature's own nav item and routes come from its module, so they cannot
 * disagree with each other. An entry point that lives in ANOTHER feature can —
 * the editor's way in to Overtime sits in `import`, and re-deriving "is overtime
 * on" there would be a second implementation free to drift from this one (the
 * static flag says default-off; only the resolved verdict knows a deployment
 * turned it on). Route the question here instead.
 */
export function isFeatureOn(name: FeatureName): boolean {
  return featureIsOn(name, isProduction())
}

let _features: { name: FeatureName; feature: FrontendFeature }[] | null = null

/** Load all enabled features. Call once before render. */
export async function initFeatures(): Promise<void> {
  const results: { name: FeatureName; feature: FrontendFeature }[] = []
  const entries = Object.entries(loaders) as [FeatureName, FeatureLoader | undefined][]

  // Dev-only features (DevTools) are for LOCAL development only — not for any deployed
  // environment, dev included. Vite's build mode is exactly that signal: the local dev
  // server runs in 'development', while every image we ship is a production build. So
  // `prod` is true in all three deployed environments and isFeatureEnabled() drops the
  // devOnly features there.
  //
  // Deliberately NOT the runtime environment from /api/config: that would keep DevTools
  // alive in deployed dev, which is what we're removing.
  const prod = isProduction()

  // Filter to only features with frontend loaders
  const frontendEntries = entries.filter(
    (entry): entry is [FeatureName, FeatureLoader] => entry[1] !== undefined
  )

  await Promise.all(
    frontendEntries
      .filter(([name]): boolean => featureIsOn(name, prod))
      .map(async ([name, load]): Promise<void> => {
        try {
          const mod = await load()
          results.push({ name, feature: mod.default })
        } catch (err) {
          // nosemgrep -- name is a typed FeatureName enum, not user input
          logger.error('Failed to load feature', err instanceof Error ? err : undefined, { feature: name })
        }
      })
  )

  // Preserve insertion order from loaders (Promise.all may resolve out of order)
  const order = frontendEntries.map(([name]): FeatureName => name)
  results.sort((a, b): number => order.indexOf(a.name) - order.indexOf(b.name))

  _features = results
}

function getFeatures(): { name: FeatureName; feature: FrontendFeature }[] {
  if (!_features) throw new Error('Features not initialized. Call initFeatures() first.')
  return _features
}

/** Nav items for core features (Dashboard area) */
export function getFeatureNavItems(): NavItemConfig[] {
  return getFeatures()
    .filter(({ name }): boolean => !isDevFeature(name))
    .flatMap(({ feature }): Array<NavItemConfig> => feature.navItems)
}

/** Nav items for dev testing features (Development Testing area) */
export function getDevFeatureNavItems(): NavItemConfig[] {
  return getFeatures()
    .filter(({ name }): boolean => isDevFeature(name))
    .flatMap(({ feature }): Array<NavItemConfig> => feature.navItems)
}

/** All routes from all enabled features */
export function getFeatureRoutes(): ReactElement[] {
  return getFeatures()
    .flatMap(({ feature }) => feature.routes)
}
